
export const normalize = (v) =>
  (v ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();


// You can optionally import i18n-iso-countries for global coverage
// import countries from "i18n-iso-countries";

export const getCountry = (customerOrAddress) => {
  const address =
    typeof customerOrAddress === "string"
      ? customerOrAddress
      : customerOrAddress?.address;

  if (!address || typeof address !== "string") return null;

  // Normalize whitespace
  const norm = address.replace(/\s+/g, " ").trim();

  // Split into chunks
  const parts = norm.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;

  let lastChunk = parts[parts.length - 1];

  // If last chunk looks like a city+postal (e.g. "0161 Oslo"), treat it as not a country
  if (/^\d{4,6}\s+\p{L}+/u.test(lastChunk)) {
    return null;
  }

  // If last chunk contains spaces + letters only (like "United States"), accept it
  if (/^[A-Za-zÀ-ÿ'’\-\s]+$/.test(lastChunk)) {
    return lastChunk;
  }

  return null;
};


// Robust city extractor for Google-style objects or plain strings
export const getCity = (customerOrAddress) => {
  const address =
    typeof customerOrAddress === "string"
      ? customerOrAddress
      : customerOrAddress?.address;

  if (!address || typeof address !== "string") return null;

  // 1) Prefer Google address_components when present
  const comps =
    typeof customerOrAddress === "object" &&
    (customerOrAddress?.address_components || customerOrAddress?.components);
  if (Array.isArray(comps)) {
    const hit = comps.find(
      (c) =>
        Array.isArray(c.types) &&
        (c.types.includes("locality") || c.types.includes("postal_town"))
    );
    if (hit?.long_name) return hit.long_name;
  }

  // 2) String heuristics
  const norm = address.replace(/\s+/g, " ").trim();

  const countryLike = new Set(
    [
      "norway","norge","usa","united states","uk","united kingdom",
      "germany","deutschland","france","italy","spain","españa","espana",
      "sweden","sverige","denmark","danmark","finland","poland","polska",
      "netherlands","nederland","belgium","belgie","belgië","belgique",
      "portugal","ireland","éire","eire","switzerland","schweiz",
      "austria","österreich","osterreich"
    ].map(s => s.toLowerCase())
  );

  const stripPostcode = (s) => {
    let x = s.trim();
    // Leading SE (Sweden): "SE-111 46 City" or "111 46 City" or "11146 City"
    x = x.replace(/^(?:SE-)?\d{3}\s?\d{2}\s+/i, "");
    // Leading generic EU: "0150 City", "75008 City", "3511 AB City"
    x = x.replace(/^(?:\d{4,5}(?:\s?[A-Z]{2})?)\s+/i, "");
    // Trailing US (e.g., "City CA 94043")
    x = x.replace(/\s+[A-Z]{2}\s*\d{5}(?:-\d{4})?$/i, "");
    // Trailing UK (e.g., "City SW1A 1AA")
    x = x.replace(/\s+[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i, "");
    // Generic trailing 4–6 digit code
    x = x.replace(/\s+\d{4,6}$/i, "");
    return x.trim();
  };

  // Comma-separated address
  if (norm.includes(",")) {
    const parts = norm.split(",").map(p => p.trim()).filter(Boolean);

    // Scan from the second chunk onward (skip street)
    for (let i = 1; i < parts.length; i++) {
      const chunk = parts[i];
      const lower = chunk.toLowerCase();
      if (countryLike.has(lower)) continue;

      // Sweden: "111 46 Stockholm", "SE-111 46 Stockholm", or "11146 Stockholm"
      let m = chunk.match(/^(?:SE-)?(\d{3}\s?\d{2})\s+(.+)$/iu);
      if (m) return stripPostcode(m[2]);

      // Generic EU: "0150 Oslo", "75008 Paris", "3511 AB Utrecht"
      m = chunk.match(/^(?:\d{4,5}(?:\s?[A-Z]{2})?)\s+(.+)$/iu);
      if (m) return stripPostcode(m[1]);

      // "City CA 94043"
      m = chunk.match(/^(.+?)\s+[A-Z]{2}\s*\d{5}(?:-\d{4})?$/iu);
      if (m) return stripPostcode(m[1]);

      // "City SW1A 1AA"
      m = chunk.match(/^(.+?)\s+[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/iu);
      if (m) return stripPostcode(m[1]);

      // Plain city name (no digits, not just a 2–3 letter code)
      if (!/\d/.test(chunk) && !/^[A-Z]{2,3}$/i.test(chunk)) {
        return stripPostcode(chunk);
      }
    }

    // Fallback: take the second-last non-country chunk
    for (let i = parts.length - 2; i >= 1; i--) {
      const chunk = parts[i];
      if (!countryLike.has(chunk.toLowerCase())) {
        return stripPostcode(chunk) || null;
      }
    }
    return null;
  }

  // No commas → look for "... 111 46 Stockholm" or "... 11146 Stockholm"
  let m = norm.match(/\b(?:SE-)?(\d{3}\s?\d{2})\s+([\p{L}\-'. ]+)$/iu);
  if (m) return stripPostcode(m[2]);

  // Generic EU: "... 0150 Oslo", "... 75008 Paris"
  m = norm.match(/\b(\d{4,5}(?:\s?[A-Z]{2})?)\s+([\p{L}\-'. ]+)$/iu);
  if (m) return stripPostcode(m[2]);

  // "... City CA 94043"
  m = norm.match(/([\p{L}\-'. ]+)\s+[A-Z]{2}\s*\d{5}(?:-\d{4})?$/iu);
  if (m) return stripPostcode(m[1]);

  // "... City SW1A 1AA"
  m = norm.match(/([\p{L}\-'. ]+)\s+[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/iu);
  if (m) return stripPostcode(m[1]);

  return null;
};
